// La mitad "de browser" de los adjuntos: `File` → `ChatAttachment` del motor.
//
// Se separa de `adjuntosChatAtlas.ts` (que es puro y testeado) porque acá hace falta
// FileReader y la subida al storage — nada de eso corre en el entorno de tests.
//
// Las IMÁGENES suben al bucket con `uploadReferenceImage`, como en el asistente
// (ADR-2026-09-08-el-chat-toma-el-motor-del-asistente): pasa por
// `normalizeImageForUpload` (HEIC/AVIF → JPEG), el modelo la VE por URL firmada y además
// queda como referencia para generar. Los archivos de TEXTO se leen y viajan inline.

import {
  uploadReferenceImage,
  uploadReferenceVideo,
  type ChatAttachment,
} from '../../pages/v2/agentAttachments';
import {
  MAX_FILAS_LEIDAS,
  resumenDePlanilla,
  type Planilla,
} from '../../pages/v2/planillas/planilla';
import { registrarPlanilla } from '../../pages/v2/planillas/planillasEnMemoria';
import {
  MAX_PAGINAS_LEIDAS,
  resumenDePdf,
  type Pdf,
} from '../../pages/v2/pdfs/pdf';
import { registrarPdf } from '../../pages/v2/pdfs/pdfsEnMemoria';
import { parsearCsv } from '../../utils/planilla/csv';
import { leerPdf } from '../../utils/pdf/leerPdf';
import { leerXlsx } from '../../utils/planilla/xlsx';
import {
  claseDeArchivo,
  extensionDe,
  MAX_BYTES_VIDEO,
  MAX_SEGUNDOS_VIDEO,
  MENSAJE_FORMATO_ADJUNTO,
  mensajeVideoGrande,
  mensajeVideoLargo,
  recortarTexto,
} from '../adjuntosChatAtlas';

const nuevoId = (prefijo: string): string => {
  try {
    return `${prefijo}-${crypto.randomUUID()}`;
  } catch {
    return `${prefijo}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
};

const leerBytes = (archivo: Blob): Promise<Uint8Array> =>
  new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onerror = () => rechazar(new Error('No pude leer el archivo.'));
    lector.onload = () => resolver(new Uint8Array(lector.result as ArrayBuffer));
    lector.readAsArrayBuffer(archivo);
  });

/** Duración del video leyendo sólo los metadatos (como Shorts Studio). 0 si no se pudo. */
const medirDuracionDeVideo = (archivo: File): Promise<number> =>
  new Promise((resolver) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      const d = v.duration;
      URL.revokeObjectURL(v.src);
      resolver(Number.isFinite(d) ? d : 0);
    };
    v.onerror = () => resolver(0);
    v.src = URL.createObjectURL(archivo);
  });

const leerTexto = (archivo: Blob): Promise<string> =>
  new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onerror = () => rechazar(new Error('No pude leer el archivo.'));
    lector.onload = () => resolver(String(lector.result || ''));
    lector.readAsText(archivo);
  });

/**
 * Convierte un archivo elegido por el usuario en un adjunto del motor.
 *
 * Tira `Error` con un mensaje ya redactado para mostrar tal cual: los llamadores no
 * tienen que traducir nada.
 */
export async function prepararAdjunto(
  archivo: File,
  companyId: string | null,
): Promise<ChatAttachment> {
  const clase = claseDeArchivo(archivo.name, archivo.type);
  if (!clase) throw new Error(MENSAJE_FORMATO_ADJUNTO);

  if (clase === 'pdf') {
    const bytes = await leerBytes(archivo);
    // `leerPdf` tira con mensaje ya redactado si el archivo no abre (dañado, con clave).
    const leido = await leerPdf(bytes, MAX_PAGINAS_LEIDAS);
    const pdf: Pdf = { nombre: archivo.name, bytes, ...leido };
    return {
      kind: 'pdf',
      id: nuevoId('pdf'),
      name: archivo.name,
      pdfId: registrarPdf(pdf),
      resumen: resumenDePdf(pdf),
      paginas: pdf.paginas,
      tieneTexto: pdf.tieneTexto,
    };
  }

  if (clase === 'planilla') {
    const ext = extensionDe(archivo.name);
    let planilla: Planilla;
    if (ext === 'xlsx') {
      // `leerXlsx` tira con mensaje ya redactado si el archivo no es un xlsx de verdad.
      planilla = { nombre: archivo.name, hojas: leerXlsx(await leerBytes(archivo)) };
    } else {
      const filas = parsearCsv(await leerTexto(archivo));
      planilla = {
        nombre: archivo.name,
        hojas: [{ nombre: archivo.name.replace(/\.[^.]+$/, ''), filas }],
      };
    }
    // Tope de filas: una planilla de 200.000 filas ocuparía la memoria de la pestaña sin
    // que nadie las vaya a mirar. Se avisa en el resumen.
    let recortada = false;
    planilla.hojas = planilla.hojas.map((h) => {
      if (h.filas.length <= MAX_FILAS_LEIDAS) return h;
      recortada = true;
      return { ...h, filas: h.filas.slice(0, MAX_FILAS_LEIDAS) };
    });
    const totalFilas = planilla.hojas.reduce((n, h) => n + h.filas.length, 0);
    if (totalFilas === 0) throw new Error('Esa planilla no tiene ninguna fila.');
    const planillaId = registrarPlanilla(planilla);
    const resumen =
      resumenDePlanilla(planilla) +
      (recortada
        ? `\n(La planilla era más grande: se leyeron las primeras ${MAX_FILAS_LEIDAS} filas por hoja.)`
        : '');
    return {
      kind: 'planilla',
      id: nuevoId('plan'),
      name: archivo.name,
      planillaId,
      resumen,
      hojas: planilla.hojas.length,
      filas: totalFilas,
    };
  }

  if (clase === 'texto') {
    const datos = recortarTexto(await leerTexto(archivo));
    if (!datos) throw new Error('Ese archivo está vacío.');
    return {
      kind: 'texto',
      id: nuevoId('txt'),
      name: archivo.name,
      text: datos,
      bytes: datos.length,
    };
  }

  if (!companyId) throw new Error('Todavía estoy cargando tu cuenta. Probá en un segundo.');

  if (clase === 'video') {
    if (archivo.size > MAX_BYTES_VIDEO) throw new Error(mensajeVideoGrande());
    const duracion = await medirDuracionDeVideo(archivo);
    if (duracion > MAX_SEGUNDOS_VIDEO) throw new Error(mensajeVideoLargo());
    const { storagePath, bucket } = await uploadReferenceVideo(archivo, companyId);
    return {
      kind: 'video',
      id: nuevoId('vid'),
      name: archivo.name,
      storagePath,
      bucket,
      bytes: archivo.size,
      duracionSeg: Math.round(duracion),
    };
  }

  const { storagePath, previewUrl } = await uploadReferenceImage(archivo, companyId);
  return { kind: 'image', id: nuevoId('img'), storagePath, previewUrl };
}
